import React from 'react';
import Layout from '@rezdy/components/Layout/DarkCentered';
import Form from '@rezdy/components/Form';
import Field from '@rezdy/components/Form/Field';
import Grid from '@rezdy/components/Grid';
import Button from '@rezdy/components/Button';
import RadioInput from '@rezdy/components/Form/FormElements/RadioInput';
import TextInput from '@rezdy/components/Form/FormElements/TextInput';
import InputCheck from '@rezdy/components/Form/FormElements/InputCheck';
import CountrySelect from '@rezdy/components/Form/FormElements/selects/CountrySelect';
import { Article, Body, Aside } from '@rezdy/components/Layout/Article';
import i18n from '../../translations';
import { CreateCompanyCard } from './CreateCompanyCard';
import PhoneInput from '@rezdy/components/Form/FormElements/PhoneInput';
import TermsAndConditions from './TermsAndConditions';
import { Title } from '../Title';
import Message from '../Message';
import VendorAgreement from './VendorAgreement';
import { IOption } from '@rezdy/components/interfaces';
import { BusinessType } from '../../constants/businessType';
import { PrefillData } from '../../services/prefillSignup';

interface ICreateCompanyProps {
    loading: boolean;
    message: {
        isDirty: boolean;
        text: string;
    };
    setMessage: (text: string, isDirty?: boolean) => void;
    onSubmit: (form: any) => {};
}

interface ICreateCompanyState {
    termsAndConditions: boolean;
    vendorAgreement?: boolean;
    businessType: string;
    isSubmitting: boolean;
    websiteCheck: boolean;
}

class CreateCompany extends React.PureComponent<ICreateCompanyProps, ICreateCompanyState> {
    countryRef = React.createRef<CountrySelect>();
    businessTypeRef = React.createRef<RadioInput>();
    phoneRef = React.createRef<PhoneInput>();
    prefillData = new PrefillData();

    businessTypes = [
        {
            // eslint-disable-next-line quotes
            label: "I'm an Operator",
            value: BusinessType.OPERATOR,
        },
        {
            // eslint-disable-next-line quotes
            label: "I'm a Reseller",
            value: BusinessType.RESELLER,
        },
    ];

    constructor(props: ICreateCompanyProps) {
        super(props);
        this.prefillData.prefillWithParams(new URL(window.location.href));
        this.state = {
            businessType: this.prefillData.businessType,
            termsAndConditions: false,
            websiteCheck: false,
            isSubmitting: false,
        };
    }

    toggleTermsAndConditions = () => {
        this.setState({
            termsAndConditions: !this.state.termsAndConditions,
        });
    };

    toggleVendorAgreement = () => {
        this.setState({
            vendorAgreement: !this.state.vendorAgreement,
        });
    };

    toggleWebsite = () => {
        this.setState({
            websiteCheck: !this.state.websiteCheck,
        });
    };

    changeBusinessType = (option: IOption) => {
        this.setState({
            businessType: option.value,
        });
    };

    isFormValid = (form: any) => {
        const { setMessage } = this.props;
        if (!this.state.termsAndConditions) {
            setMessage('Please confirm terms and conditions', true);
            return false;
        }
        if (this.state.businessType === BusinessType.RESELLER && !this.state.vendorAgreement) {
            setMessage('Please confirm vendor agreement', true);
            return false;
        }
        const keys = Object.keys(form);
        for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            const element = form[key];
            if (element === undefined) {
                const field = i18n.t(key).toLowerCase();
                setMessage(`Please provide ${field}`, true);
                return false;
            }
        }
        return true;
    };

    onSubmit = (formData: any) => {
        const { loading } = this.props;
        const { isSubmitting } = this.state;

        this.setState(
            {
                isSubmitting: true,
            },
            () => {
                if (!isSubmitting && !loading) {
                    if (this.isFormValid(formData)) {
                        if (this.state.vendorAgreement) {
                            formData['vendorAgreementAccepted'] = true;
                        }
                        this.props.onSubmit(formData);
                    }
                    this.setState({
                        isSubmitting: false,
                    });
                }
            }
        );
    };

    renderFormMsg = () => {
        const { message } = this.props;
        const html = i18n.t(message.text);
        if (message.text) {
            return (
                <Message
                    as="p"
                    isDirty={message.isDirty}
                    align="center"
                    dangerouslySetInnerHTML={{
                        __html: html,
                    }}
                />
            );
        }
        return null;
    };

    renderForm = () => {
        const { loading } = this.props;
        const signUpButtonLabel =
            this.state.businessType === BusinessType.RESELLER
                ? 'resellerSignUpButtonLabel'
                : 'operatorSignUpButtonLabel';
        return (
            <Form onSubmit={this.onSubmit} refs={[this.countryRef, this.businessTypeRef, this.phoneRef]}>
                <Grid.Container rowGap="24px">
                    <Title>Setup your business</Title>
                    {this.renderFormMsg()}

                    <Field label={i18n.t('business')} name="businessName">
                        <TextInput />
                    </Field>
                    <RadioInput
                        options={this.businessTypes}
                        onChange={this.changeBusinessType}
                        cols="repeat(auto-fit, minmax(180px, 1fr))"
                        colGap="8px"
                        rowGap="8px"
                        id="businessType"
                        ref={this.businessTypeRef}
                        defaultValue={this.state.businessType}
                    />
                    <Grid.Container rowGap="12px">
                        <Field label={i18n.t('website')} name="companyUrl">
                            <TextInput placeholder="example.com" disabled={this.state.websiteCheck} />
                        </Field>
                        <InputCheck
                            // eslint-disable-next-line quotes
                            inlineLabel={i18n.t("I don't have a website ")}
                            value={this.state.websiteCheck}
                            onChange={this.toggleWebsite}
                        />
                    </Grid.Container>
                    <Field label={i18n.t('phoneNumber')} name="phoneNumber">
                        <PhoneInput testId="phone-input" ref={this.phoneRef} />
                    </Field>
                    <Field label={i18n.t('countryCode')} name="countryCode">
                        <CountrySelect testId="country-code" ref={this.countryRef} />
                    </Field>
                    {/* Terms and conditions */}
                    <InputCheck
                        inlineLabel={<TermsAndConditions />}
                        value={this.state.termsAndConditions}
                        onChange={this.toggleTermsAndConditions}
                    />
                    {this.state.businessType === BusinessType.RESELLER && (
                        <InputCheck
                            id={'vendorAgreementAccepted'}
                            inlineLabel={<VendorAgreement />}
                            value={this.state.vendorAgreement}
                            onChange={this.toggleVendorAgreement}
                        />
                    )}
                    <Button
                        type="submit"
                        action="primary"
                        width="100%"
                        loading={loading}
                        tight
                        name="company-signup-complete"
                    >
                        {i18n.t(signUpButtonLabel)}
                    </Button>
                </Grid.Container>
            </Form>
        );
    };

    render() {
        return (
            <Layout>
                <Article>
                    <Body>{this.renderForm()}</Body>
                    <Aside>
                        <CreateCompanyCard />
                    </Aside>
                </Article>
            </Layout>
        );
    }
}

export default CreateCompany;
